import { Component, computed, inject, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { Observable, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MaterialModule } from '../../../../../shared/material.module';
import { TabToolbarComponent } from '../tab-toolbar/tab-toolbar.component';
import { KeywordInputComponent } from '../keyword-input/keyword-input.component';
import { HasUnsavedChanges } from '../../../../../guards/unsaved-changes.guard';
import { ConfirmationDialogService } from '../../../../../services/confirmation-dialog.service';
import { Helpers } from '../../../../../utilities/helpers';
import { LutService } from '../../../../../services/lut.service';
import { CompanyEnergyWaterService } from '../../../../../services/companies/company-energy-water.service';
import { CompanyEnergyWaterDto, UpsertEnergyWaterRequest } from '../../../../../models/api/companyEnergyWaterDto';
import { CsrMeasurementUnit } from '../../../../../models/csrMeasurementUnit';
import { ApiErrorResponse } from '../../../../../models/api/apiErrorResponse';
import { ErrorCodes } from '../../../../../models/api/errorCodes';
import { selectTabYear, selectDefaultYear } from '../../../../../store/selectors/company-form.selectors';
import { setTabYear } from '../../../../../store/actions/company-form.actions';
import { NumberFormatDirective } from '../../../../../directives/number-format.directive';
import { CompanyKeywordService } from '../../../../../services/companies/company-keyword.service';
import { CompanyKeywordDto, CompanyKeywordUpdateRequest } from '../../../../../models/api/companyKeywordDto';
import { KeywordType } from '../../../../../models/enums/keywordType.enum';
import { buildKeywordRequests } from '../../../../../utilities/keyword-helpers';
import { saveTabThenKeywords } from '../../../../../utilities/tab-save';
import { serverErrorMessage } from '../../../../../utilities/api-error';
import { CurrentUserState } from '../../../../../state/current-user.state';
import { ReadOnlyTabDirective } from '../../../../../directives/read-only-tab.directive';
import { AppUserCompanyAccessType } from '../../../../../models/enums/appUserCompanyAccessType.enum';

interface EwRow {
  key: string;
  label: string;
  bold?: boolean;
  indent?: boolean;
  showPct?: boolean;
  pctBase?: string;
  unitGroup?: 'energy' | 'energyIntensity' | 'water' | 'waterIntensity';
}

@Component({
  selector: 'app-energy-water-tab',
  imports: [
    ReactiveFormsModule,
    MaterialModule,
    NgxSpinnerModule,
    TabToolbarComponent,
    KeywordInputComponent,
    NumberFormatDirective,
    ReadOnlyTabDirective,
  ],
  templateUrl: './energy-water-tab.component.html',
  styleUrl: './energy-water-tab.component.scss'
})
export class EnergyWaterTabComponent implements OnInit, OnChanges, OnDestroy, HasUnsavedChanges {
  @Input() keywords: CompanyKeywordDto[] = [];
  @Output() keywordsUpdated = new EventEmitter<CompanyKeywordDto[]>();

  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private lutService = inject(LutService);
  private energyWaterService = inject(CompanyEnergyWaterService);
  private companyKeywordService = inject(CompanyKeywordService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private toast = inject(HotToastService);
  private spinnerService = inject(NgxSpinnerService);
  private currentUserState = inject(CurrentUserState);
  private destroy$ = new Subject<void>();
  private pctLastEdited: Record<string, 'num' | 'pct'> = {};
  private energyPairLastEdited: 'directEnergy' | 'indirectEnergyUse' | 'directEnergyPct' | 'indirectEnergyUsePct' | null = null;
  private waterPairLastEdited: 'freshWaterWithdrawalTotal' | 'otherWaterSources' | 'freshWaterWithdrawalTotalPct' | 'otherWaterSourcesPct' | null = null;
  private readonly parentChildMap: { parent: string; children: string[] }[] = [
    { parent: 'oilFuels', children: ['dieselFuel', 'naturalGas', 'propaneGas', 'lpgFuel', 'coal', 'nuclearEnergy', 'petrol', 'otherOilFuels'] },
  ];

  /** Tab held read-only: the form is disabled by ReadOnlyTabDirective and row actions are hidden. */
  readonly isReadOnly = computed(() =>
    this.currentUserState.isCompanyTabReadOnly(AppUserCompanyAccessType.EnergyWater));

  selectedYear: string = this.currentUserState.defaultMeasurementYear();
  companyId: string | null = null;
  showYearWarning = false;
  showKeywords = true;
  otherMeasurement: string = '';
  otherData: CompanyEnergyWaterDto | null = null;

  allUnits: CsrMeasurementUnit[] = [];
  energyUnits: CsrMeasurementUnit[] = [];
  energyIntensityUnits: CsrMeasurementUnit[] = [];
  waterUnits: CsrMeasurementUnit[] = [];
  waterIntensityUnits: CsrMeasurementUnit[] = [];

  energyRows: EwRow[] = [
    { key: 'energyUse', label: 'Energy Use', bold: true, showPct: true, pctBase: 'energyUse' },
    { key: 'directEnergy', label: 'Direct Energy Use', showPct: true, pctBase: 'energyUse' },
    { key: 'indirectEnergyUse', label: 'Indirect Energy Use', showPct: true, pctBase: 'energyUse' },
    { key: 'renewableEnergyTotal', label: 'Renewable Energy Total', bold: true, showPct: true, pctBase: 'energyUse' },
    { key: 'oilFuels', label: 'Oil/Fuels', bold: true, showPct: true, pctBase: 'energyUse' },
    { key: 'dieselFuel', label: 'Diesel fuel', indent: true, showPct: true, pctBase: 'energyUse' },
    { key: 'naturalGas', label: 'Natural Gas', indent: true, showPct: true, pctBase: 'energyUse' },
    { key: 'propaneGas', label: 'Propane gas', indent: true, showPct: true, pctBase: 'energyUse' },
    { key: 'lpgFuel', label: 'LPG fuel', indent: true, showPct: true, pctBase: 'energyUse' },
    { key: 'coal', label: 'Coal', indent: true, showPct: true, pctBase: 'energyUse' },
    { key: 'nuclearEnergy', label: 'Nuclear Energy', indent: true, showPct: true, pctBase: 'energyUse' },
    { key: 'petrol', label: 'Petrol', indent: true, showPct: true, pctBase: 'energyUse' },
    { key: 'otherOilFuels', label: 'Other (Oil/Fuels)', indent: true, showPct: true, pctBase: 'energyUse' },
    { key: 'electricity', label: 'Electricity', bold: true, showPct: true, pctBase: 'energyUse' },
    { key: 'districtHeating', label: 'District Heating', bold: true, showPct: true, pctBase: 'energyUse' },
    { key: 'districtCooling', label: 'District Cooling', bold: true, showPct: true, pctBase: 'energyUse' },
    { key: 'steam', label: 'Steam', bold: true, showPct: true, pctBase: 'energyUse' },
    { key: 'energyIntensity', label: 'Energy Intensity', showPct: false, unitGroup: 'energyIntensity' },
  ];

  waterRows: EwRow[] = [
    { key: 'waterDischarged', label: 'Water Discharged', showPct: false },
    { key: 'waterConsumption', label: 'Water consumption', showPct: false },
    { key: 'waterRecycled', label: 'Water Recycled', showPct: true, pctBase: 'waterWithdrawalTotal' },
    { key: 'waterIntensity', label: 'Water Intensity', showPct: false, unitGroup: 'waterIntensity' },
    { key: 'waterWithdrawalTotal', label: 'Water Withdrawal Total/ Water Sources', bold: true, showPct: true, pctBase: 'waterWithdrawalTotal' },
    { key: 'freshWaterWithdrawalTotal', label: 'Fresh Water Withdrawal Total', indent: true, showPct: true, pctBase: 'waterWithdrawalTotal' },
    { key: 'otherWaterSources', label: 'Other Water Sources', indent: true, showPct: true, pctBase: 'waterWithdrawalTotal' },
    { key: 'surfaceWater', label: 'Surface Water (wetland,river,lakes)', showPct: true, pctBase: 'waterWithdrawalTotal' },
    { key: 'groundWater', label: 'Ground Water', showPct: true, pctBase: 'waterWithdrawalTotal' },
    { key: 'seaWater', label: 'Sea Water', showPct: true, pctBase: 'waterWithdrawalTotal' },
    { key: 'municipalWater', label: 'Municipal Water', showPct: true, pctBase: 'waterWithdrawalTotal' },
    { key: 'thirdPartyWater', label: 'Third-Party Water (Produced Water, ect.)', showPct: true, pctBase: 'waterWithdrawalTotal' },
  ];

  /** Energy rows whose percentage is a share of Energy Use — Energy Use itself is the base, not a share. */
  private readonly energyShareKeys = this.energyRows
    .filter(r => r.showPct && r.key !== 'energyUse')
    .map(r => r.key);

  form: FormGroup = this.buildForm();

  get hasChanges(): boolean {
    return this.form.dirty;
  }

  hasUnsavedChanges(): boolean {
    return this.hasChanges;
  }

  private readonly keywordMapping: { formField: string; type: KeywordType }[] = [
    { formField: 'energy', type: KeywordType.Energy },
    { formField: 'water', type: KeywordType.Water },
  ];

  ngOnInit(): void {
    this.companyId = this.route.snapshot.paramMap.get('id') ?? null;

    const storedYear = this.store.selectSignal(selectTabYear('energyWater'))();
    const defaultYear = this.store.selectSignal(selectDefaultYear)();
    if (storedYear) {
      this.selectedYear = storedYear;
    } else if (defaultYear) {
      this.selectedYear = defaultYear;
      this.store.dispatch(setTabYear({ tabId: 'energyWater', year: defaultYear }));
    } else {
      this.store.dispatch(setTabYear({ tabId: 'energyWater', year: this.selectedYear }));
    }

    this.patchKeywords();
    this.setupPctAutoCalc();
    this.loadLookupData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['keywords'] && !changes['keywords'].firstChange) {
      this.patchKeywords();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private patchKeywords(): void {
    const patch: Record<string, string> = {};
    for (const mapping of this.keywordMapping) {
      const kw = this.keywords.find(k => k.type === mapping.type);
      patch[mapping.formField] = kw?.value ?? '';
    }
    this.form.get('keywords')!.patchValue(patch, { emitEvent: false });
    this.form.markAsPristine();
  }

  onYearChange(year: string): void {
    if (this.hasChanges) {
      this.confirmationDialog.openConfirmationDialog(
        'Unsaved Changes',
        'You have unsaved changes. Are you sure you want to switch measurement?',
        'Switch',
        'Stay',
        'swap_horiz',
        'edit'
      ).subscribe(confirmed => {
        if (confirmed) {
          this.applyYearChange(year);
        } else {
          const current = this.selectedYear;
          this.selectedYear = '';
          setTimeout(() => this.selectedYear = current);
        }
      });
    } else {
      this.applyYearChange(year);
    }
  }

  onOtherMeasurementChange(year: string): void {
    this.otherMeasurement = year;
    if (!year || !this.companyId) {
      this.otherData = null;
      return;
    }
    this.loadOtherMeasurementData();
  }

  private getSaveDescription(): string {
    const hasKeywordChanges = this.form.get('keywords')!.dirty;
    const hasEwChanges = this.form.dirty && !hasKeywordChanges;
    if (hasEwChanges && hasKeywordChanges) return 'energy & water data and keywords';
    if (hasKeywordChanges) return 'keywords';
    return 'energy & water data';
  }

  onSave(): void {
    if (!this.companyId) return;
    this.performSave();
  }

  formatNumber(value: number | null | undefined): string {
    if (value == null) return '';
    return value.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
  }

  getConverted(key: string): number | null {
    const value = this.form.get(key)?.value;
    const unitId = this.form.get(key + 'UnitId')?.value;
    if (value == null || !unitId) return null;
    const unit = this.allUnits.find(u => u.id === unitId);
    if (unit?.converter == null) return null;
    return value * unit.converter;
  }

  getConvertedRestatement(key: string): number | null {
    const value = this.form.get(key + 'Restatement')?.value;
    const unitId = this.form.get(key + 'UnitId')?.value;
    if (value == null || !unitId) return null;
    const unit = this.allUnits.find(u => u.id === unitId);
    if (unit?.converter == null) return null;
    return value * unit.converter;
  }

  /**
   * The Other Measurement's converted figure: that year's value in that year's unit. The
   * comparison cells used to repeat the current year's conversion instead.
   */
  getOtherConverted(key: string, suffix: '' | 'Restatement' = ''): number | null {
    const value = this.getOtherValue(key + suffix);
    const unitId = this.getOtherStringValue(key + 'UnitId');
    if (value == null || !unitId) return null;
    const unit = this.allUnits.find(u => u.id === unitId);
    if (unit?.converter == null) return null;
    return value * unit.converter;
  }

  getUnitsForRow(row: EwRow, fallback: CsrMeasurementUnit[]): CsrMeasurementUnit[] {
    switch (row.unitGroup) {
      case 'energyIntensity': return this.energyIntensityUnits;
      case 'waterIntensity': return this.waterIntensityUnits;
      default: return fallback;
    }
  }

  getUnitName(unitId: string | null | undefined): string {
    if (!unitId) return '';
    return this.allUnits.find(u => u.id === unitId)?.name ?? '';
  }

  getOtherValue(key: string): number | null | undefined {
    if (!this.otherData) return undefined;
    return (this.otherData as any)[key];
  }

  getOtherStringValue(key: string): string | null | undefined {
    if (!this.otherData) return undefined;
    return (this.otherData as any)[key];
  }

  private buildForm(): FormGroup {
    const controls: Record<string, any> = {};
    for (const row of [...this.energyRows, ...this.waterRows]) {
      controls[row.key] = [null as number | null];
      controls[row.key + 'Restatement'] = [null as number | null];
      controls[row.key + 'UnitId'] = [''];
      if (row.showPct) {
        controls[row.key + 'Pct'] = [null as number | null];
      }
    }
    controls['comments'] = [''];
    controls['keywords'] = this.fb.group({
      energy: [''],
      water: ['']
    });
    return this.fb.group(controls);
  }

  private calcPct(numerator: number | null, denominator: number | null): number | null {
    if (numerator == null || denominator == null || denominator === 0) return null;
    return Math.round((numerator * 100 / denominator) * 100) / 100;
  }

  /**
   * A share of Energy Use only means something when the row is measured in Energy Use's own unit:
   * a figure in MWh is not a percentage of a total in GJ.
   */
  private pctAllowed(key: string): boolean {
    if (!this.energyShareKeys.includes(key)) return true;
    return (this.form.get(key + 'UnitId')!.value ?? '') === (this.form.get('energyUseUnitId')!.value ?? '');
  }

  /** The % input is only offered where a percentage can legitimately be calculated. */
  canShowPct(row: EwRow): boolean {
    return !!row.showPct && this.pctAllowed(row.key);
  }

  /** Every percentage write goes through here, so an incompatible unit clears the field instead. */
  private setPct(key: string, value: number | null): void {
    this.form.get(key + 'Pct')?.setValue(this.pctAllowed(key) ? value : null, { emitEvent: false });
  }

  private setupPctAutoCalc(): void {
    const allRows = [...this.energyRows, ...this.waterRows];
    const pctRows = allRows.filter(r => r.showPct);
    const baseKeys = [...new Set(pctRows.map(r => r.pctBase!))];
    const ctrl = (name: string) => this.form.get(name)!;

    for (const row of pctRows) {
      if (row.key === row.pctBase) continue;

      ctrl(row.key).valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.pctLastEdited[row.key] = 'num';
        this.syncField(row.key, row.pctBase!, 'num');
      });

      ctrl(row.key + 'Pct').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.pctLastEdited[row.key] = 'pct';
        this.syncField(row.key, row.pctBase!, 'pct');
      });
    }

    for (const baseKey of baseKeys) {
      ctrl(baseKey).valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        const dependentRows = pctRows.filter(r => r.pctBase === baseKey && r.key !== baseKey);
        for (const row of dependentRows) {
          const side = this.pctLastEdited[row.key] === 'pct' ? 'pct' : 'num';
          this.syncField(row.key, baseKey, side);
        }
        this.setPct(baseKey, ctrl(baseKey).value != null ? 100 : null);
      });
    }

    // A share of Energy Use is only valid in Energy Use's own unit, so both sides of the
    // comparison have to be watched: each row's unit and Energy Use's own.
    const energyShareRows = this.energyRows.filter(r => this.energyShareKeys.includes(r.key));
    for (const key of ['energyUse', ...this.energyShareKeys]) {
      ctrl(key + 'UnitId').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        for (const row of energyShareRows) {
          // A percentage the user typed stays as typed while it is still valid; anything else is
          // re-derived from the value, which also clears a row the unit change just invalidated.
          const keepTyped = this.pctLastEdited[row.key] === 'pct'
            && this.pctAllowed(row.key)
            && ctrl(row.key + 'Pct').value != null;
          this.syncField(row.key, row.pctBase!, keepTyped ? 'pct' : 'num');
        }
      });
    }

    // Energy Use = Direct Energy + Indirect Energy Use (like permanent + temporary = total)
    const energyPairFields: Array<'directEnergy' | 'indirectEnergyUse' | 'directEnergyPct' | 'indirectEnergyUsePct'> =
      ['directEnergy', 'indirectEnergyUse', 'directEnergyPct', 'indirectEnergyUsePct'];
    for (const field of energyPairFields) {
      ctrl(field).valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.energyPairLastEdited = field;
        this.syncEnergyPair(field);
      });
    }

    ctrl('energyUse').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.syncEnergyPair('energyUse', this.energyPairLastEdited);
    });

    // Parent-child auto-sum (e.g. Oil/Fuels = sum of subcategories)
    for (const mapping of this.parentChildMap) {
      for (const child of mapping.children) {
        ctrl(child).valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
          this.sumIntoParent(mapping.parent, mapping.children);
        });
      }
    }

    // Water Withdrawal Total = Fresh Water + Other Water Sources (like Direct + Indirect = Energy Use)
    const waterPairFields: Array<'freshWaterWithdrawalTotal' | 'otherWaterSources' | 'freshWaterWithdrawalTotalPct' | 'otherWaterSourcesPct'> =
      ['freshWaterWithdrawalTotal', 'otherWaterSources', 'freshWaterWithdrawalTotalPct', 'otherWaterSourcesPct'];
    for (const field of waterPairFields) {
      ctrl(field).valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.waterPairLastEdited = field;
        this.syncWaterPair(field);
      });
    }

    ctrl('waterWithdrawalTotal').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.syncWaterPair('waterWithdrawalTotal', this.waterPairLastEdited);
      this.calcWaterConsumption();
    });

    // Water Consumption = Water Withdrawal Total - Water Discharged
    ctrl('waterDischarged').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.calcWaterConsumption();
    });
  }

  private sumIntoParent(parentKey: string, childKeys: string[]): void {
    const values = childKeys.map(k => this.form.get(k)!.value as number | null);
    const nums = values.filter((v): v is number => v != null);
    const sum = nums.length > 0 ? nums.reduce((a, b) => a + b, 0) : null;
    this.form.get(parentKey)!.setValue(sum, { emitEvent: false });

    const allRows = [...this.energyRows, ...this.waterRows];
    const parentRow = allRows.find(r => r.key === parentKey);
    if (parentRow?.pctBase) {
      const base = this.form.get(parentRow.pctBase)!.value as number | null;
      this.setPct(parentKey, parentKey === parentRow.pctBase && sum != null ? 100 : this.calcPct(sum, base));
    }

    // Re-sync pct for children since parent (base) changed
    if (parentRow?.pctBase === parentKey) {
      const childRows = allRows.filter(r => r.pctBase === parentKey && r.key !== parentKey);
      for (const row of childRows) {
        const side = this.pctLastEdited[row.key] === 'pct' ? 'pct' : 'num';
        this.syncField(row.key, parentKey, side);
      }
    }
  }

  private syncEnergyPair(
    trigger: string,
    lastEdited: string | null = trigger
  ): void {
    const f = this.form;
    const total = f.get('energyUse')!.value as number | null;
    const set = (name: string, value: number | null) =>
      f.get(name)!.setValue(value, { emitEvent: false });
    const round2 = (v: number): number => Math.round(v * 100) / 100;

    const effective = trigger === 'energyUse' ? lastEdited : trigger;

    if (total != null) {
      if (effective === 'directEnergy') {
        const a = f.get('directEnergy')!.value as number | null;
        if (a != null) set('indirectEnergyUse', Math.max(0, total - a));
      } else if (effective === 'indirectEnergyUse') {
        const b = f.get('indirectEnergyUse')!.value as number | null;
        if (b != null) set('directEnergy', Math.max(0, total - b));
      } else if (effective === 'directEnergyPct') {
        const aPct = f.get('directEnergyPct')!.value as number | null;
        if (aPct != null) {
          const a = Math.round(total * aPct / 100 * 100) / 100;
          set('directEnergy', a);
          set('indirectEnergyUse', Math.max(0, total - a));
          this.setPct('indirectEnergyUse', round2(100 - aPct));
          return;
        }
      } else if (effective === 'indirectEnergyUsePct') {
        const bPct = f.get('indirectEnergyUsePct')!.value as number | null;
        if (bPct != null) {
          const b = Math.round(total * bPct / 100 * 100) / 100;
          set('indirectEnergyUse', b);
          set('directEnergy', Math.max(0, total - b));
          this.setPct('directEnergy', round2(100 - bPct));
          return;
        }
      }
    }

    // Recalc percentages from values
    const a = f.get('directEnergy')!.value as number | null;
    const aPct = this.calcPct(a, total);
    this.setPct('directEnergy', aPct);
    this.setPct('indirectEnergyUse', aPct != null ? round2(100 - aPct) : null);

    // Auto-calc energyUse if both parts are present but total is missing
    if (total == null) {
      const direct = f.get('directEnergy')!.value as number | null;
      const indirect = f.get('indirectEnergyUse')!.value as number | null;
      if (direct != null && indirect != null) {
        set('energyUse', direct + indirect);
      }
    }
  }

  private syncWaterPair(
    trigger: string,
    lastEdited: string | null = trigger
  ): void {
    const f = this.form;
    const total = f.get('waterWithdrawalTotal')!.value as number | null;
    const set = (name: string, value: number | null) =>
      f.get(name)!.setValue(value, { emitEvent: false });
    const round2 = (v: number): number => Math.round(v * 100) / 100;

    const effective = trigger === 'waterWithdrawalTotal' ? lastEdited : trigger;

    if (total != null) {
      if (effective === 'freshWaterWithdrawalTotal') {
        const a = f.get('freshWaterWithdrawalTotal')!.value as number | null;
        if (a != null) set('otherWaterSources', Math.max(0, total - a));
      } else if (effective === 'otherWaterSources') {
        const b = f.get('otherWaterSources')!.value as number | null;
        if (b != null) set('freshWaterWithdrawalTotal', Math.max(0, total - b));
      } else if (effective === 'freshWaterWithdrawalTotalPct') {
        const aPct = f.get('freshWaterWithdrawalTotalPct')!.value as number | null;
        if (aPct != null) {
          const a = Math.round(total * aPct / 100 * 100) / 100;
          set('freshWaterWithdrawalTotal', a);
          set('otherWaterSources', Math.max(0, total - a));
          set('otherWaterSourcesPct', round2(100 - aPct));
          return;
        }
      } else if (effective === 'otherWaterSourcesPct') {
        const bPct = f.get('otherWaterSourcesPct')!.value as number | null;
        if (bPct != null) {
          const b = Math.round(total * bPct / 100 * 100) / 100;
          set('otherWaterSources', b);
          set('freshWaterWithdrawalTotal', Math.max(0, total - b));
          set('freshWaterWithdrawalTotalPct', round2(100 - bPct));
          return;
        }
      }
    }

    const a = f.get('freshWaterWithdrawalTotal')!.value as number | null;
    const aPct = this.calcPct(a, total);
    set('freshWaterWithdrawalTotalPct', aPct);
    set('otherWaterSourcesPct', aPct != null ? round2(100 - aPct) : null);

    if (total == null) {
      const fresh = f.get('freshWaterWithdrawalTotal')!.value as number | null;
      const other = f.get('otherWaterSources')!.value as number | null;
      if (fresh != null && other != null) {
        set('waterWithdrawalTotal', fresh + other);
      }
    }
  }

  private calcWaterConsumption(): void {
    const total = this.form.get('waterWithdrawalTotal')!.value as number | null;
    const discharged = this.form.get('waterDischarged')!.value as number | null;
    if (total != null && discharged != null) {
      this.form.get('waterConsumption')!.setValue(Math.max(0, total - discharged), { emitEvent: false });
    }
  }

  private syncField(numField: string, baseField: string, side: 'num' | 'pct'): void {
    const pctField = numField + 'Pct';
    const base = this.form.get(baseField)!.value as number | null;
    const set = (name: string, value: number | null) =>
      this.form.get(name)!.setValue(value, { emitEvent: false });

    if (side === 'pct') {
      const pct = this.form.get(pctField)!.value as number | null;
      if (base != null && pct != null) {
        set(numField, Math.round(base * pct / 100 * 100) / 100);
      }
      for (const mapping of this.parentChildMap) {
        if (mapping.children.includes(numField)) {
          this.sumIntoParent(mapping.parent, mapping.children);
        }
      }
    } else {
      const num = this.form.get(numField)!.value as number | null;
      this.setPct(numField, this.calcPct(num, base));
    }
  }

  private applyYearChange(year: string): void {
    this.selectedYear = year;
    this.store.dispatch(setTabYear({ tabId: 'energyWater', year }));
    this.loadData();
  }

  private loadOtherMeasurementData(): void {
    if (!this.companyId || !this.otherMeasurement) return;
    this.energyWaterService.get(this.companyId, parseInt(this.otherMeasurement)).subscribe({
      next: resource => {
        this.otherData = resource.state === 'present' ? resource.data : null;
      },
      error: () => {
        this.otherData = null;
      }
    });
  }

  private performSave(): void {
    if (!this.companyId) return;

    const request = this.buildRequest();

    const keywordsValue = this.form.get('keywords')!.value;
    const keywordRequests: CompanyKeywordUpdateRequest[] = buildKeywordRequests(keywordsValue, this.keywordMapping, this.keywords);

    const hasEwData = Object.keys(this.form.value).some(k => k !== 'keywords' && this.form.get(k)?.dirty);
    const hasKeywordData = keywordRequests.length > 0 || this.keywords.some(
      k => this.keywordMapping.some(m => m.type === k.type)
    );

    if (!hasEwData && !hasKeywordData) {
      this.toast.info('No changes to save');
      return;
    }

    const description = this.getSaveDescription();
    this.spinnerService.show();

    const saveCalls: Observable<any>[] = [];
    saveCalls.push(hasEwData ? this.energyWaterService.upsert(this.companyId!, request) : of(null));
    saveTabThenKeywords(saveCalls, this.companyKeywordService.upsert(this.companyId!, keywordRequests)).subscribe({
      next: ([_ewResult, keywordsResult]) => {
        this.showYearWarning = false;
        this.keywordsUpdated.emit(keywordsResult as CompanyKeywordDto[]);
        this.form.markAsPristine();
        this.spinnerService.hide();
        this.toast.success(`Saved ${description} successfully`);
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error(serverErrorMessage(err, `Failed to save ${description}`));
        console.error('Save error:', err);
      }
    });
  }

  private buildRequest(): UpsertEnergyWaterRequest {
    const v = this.form.value;
    const request: any = {
      year: parseInt(this.selectedYear),
      comments: v.comments || null
    };
    for (const row of [...this.energyRows, ...this.waterRows]) {
      request[row.key] = v[row.key] ?? null;
      request[row.key + 'Restatement'] = v[row.key + 'Restatement'] ?? null;
      request[row.key + 'UnitId'] = v[row.key + 'UnitId'] || null;
      if (row.showPct) {
        request[row.key + 'Pct'] = v[row.key + 'Pct'] ?? null;
      }
    }
    return request as UpsertEnergyWaterRequest;
  }

  private loadLookupData(): void {
    this.spinnerService.show();

    this.lutService.getCsrMeasurementUnits().subscribe({
      next: (units) => {
        this.allUnits = units.sort((a, b) => a.name.localeCompare(b.name));
        // Type 2 = Energy
        this.energyUnits = this.allUnits.filter(u => u.type === 2);
        // Type 6 = Energy Intensity
        this.energyIntensityUnits = this.allUnits.filter(u => u.type === 6);
        // Type 4 = Water
        this.waterUnits = this.allUnits.filter(u => u.type === 4);
        // Type 7 = Water Intensity
        this.waterIntensityUnits = this.allUnits.filter(u => u.type === 7);
        this.loadData();
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error('Failed to load measurement units');
        console.error('Lookup data error:', err);
      }
    });
  }

  private loadData(): void {
    if (!this.companyId) {
      this.spinnerService.hide();
      return;
    }

    this.spinnerService.show();
    this.energyWaterService.get(this.companyId, parseInt(this.selectedYear)).subscribe({
      next: resource => {
        const formValues = resource.state === 'present'
          ? resource.data
          : (resource.state === 'carried-forward' ? resource.carriedForward?.values : null);
        this.showYearWarning = resource.state === 'carried-forward' || resource.state === 'absent';
        if (formValues) {
          this.patchForm(formValues);
        } else {
          this.resetForm();
        }
        this.spinnerService.hide();
      },
      error: (error: any) => {
        this.spinnerService.hide();
        const apiError = error.error as ApiErrorResponse;

        if (apiError?.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
          this.resetForm();
        } else {
          this.toast.error('Error loading energy & water data');
        }

        console.error('Energy & water error:', error);
      }
    });
  }

  private patchForm(data: CompanyEnergyWaterDto): void {
    const patch: Record<string, any> = {};
    for (const row of [...this.energyRows, ...this.waterRows]) {
      patch[row.key] = (data as any)[row.key];
      patch[row.key + 'Restatement'] = (data as any)[row.key + 'Restatement'];
      patch[row.key + 'UnitId'] = (data as any)[row.key + 'UnitId'] || '';
      if (row.showPct) {
        patch[row.key + 'Pct'] = (data as any)[row.key + 'Pct'];
      }
    }
    patch['comments'] = data.comments || '';
    this.form.patchValue(patch, { emitEvent: false });
    this.recalcAllPct();
    this.form.markAsPristine();
  }

  private recalcAllPct(): void {
    const allRows = [...this.energyRows, ...this.waterRows];
    for (const row of allRows) {
      if (!row.showPct) continue;
      const pctControl = this.form.get(row.key + 'Pct');
      if (!pctControl) continue;
      if (!this.pctAllowed(row.key)) {
        pctControl.setValue(null, { emitEvent: false });
        continue;
      }
      if (pctControl.value != null) {
        this.pctLastEdited[row.key] = 'pct';
        continue;
      }
      if (row.key === row.pctBase) {
        const baseVal = this.form.get(row.key)!.value;
        pctControl.setValue(baseVal != null ? 100 : null, { emitEvent: false });
      } else {
        const num = this.form.get(row.key)!.value as number | null;
        const base = this.form.get(row.pctBase!)!.value as number | null;
        pctControl.setValue(this.calcPct(num, base), { emitEvent: false });
      }
    }
  }

  private resetForm(): void {
    const keywordsValue = this.form.get('keywords')!.value;
    this.form.reset();
    this.form.get('keywords')!.patchValue(keywordsValue, { emitEvent: false });
    this.form.markAsPristine();
  }
}
